<template>
  <canvas-webgl
    v-if="useWebgl && !isDeepDeeAnnotation"
    :img="img"
    :colorSettings="colorSettings"
    :octColorSettings="octColorSettings"
    :thicknessMapColorSettings="thicknessMapColorSettings"
    :canvasSize="canvasSize"
    :imgSize="imgSize"
    :transform="transform"
    :isFlipped="isFlipped"
    :isFlipped90="isFlipped90"
    :isFlipped270="isFlipped270"
    :flippedX="flippedX"
    :autodraw="autodraw"
    :lastImgSize="lastImgSize"
    :usePixelCanvas="usePixelCanvas"
    :bounds="bounds"
    :componentName="componentName"
    :isSideBySide="isSideBySide"
    ref="canvasWebgl"/>
  <canvas-image
    v-else
    :img="processedImg"
    :usePixelCanvas="usePixelCanvas"
    :canvasSize="canvasSize"
    :imgSize="imgSize"
    :transform="transform"
    :isFlipped="isFlipped"
    :isFlipped90="isFlipped90"
    :isFlipped270="isFlipped270"
    :flippedX="flippedX"
    :autodraw="autodraw"
    :bounds="bounds"
    :isDeepDeeAnnotation="isDeepDeeAnnotation"
    ref="canvasImage"/>
</template>

<script>
import canvasImage from "./CanvasImage";
import canvasWebgl from "./CanvasWebgl";
import { mapGetters } from "vuex";
import {
  hasCustomPixelColorSettings,
  hasCustomOCTColorSettings,
  hasCustomThicknessColorSettings,
  applyPixelShadingRules,
  applyOCTPixelShading,
  drawShadedImage,
  setMinimalSize
} from "../canvasUtils";
import { debounce } from "../../helpers/utils";
import { defaultSize, setSize } from "../geometry";
export default {
  name: "pixelShadedCanvas",
  components: { canvasImage, canvasWebgl },
  props: {
    img: [HTMLImageElement, HTMLCanvasElement],
    colorSettings: Object,
    octColorSettings: Object,
    thicknessMapColorSettings: Object,
    imgSize: Object,
    canvasSize: Object,
    transform: Object,
    isFlipped: Boolean,
    isFlipped90: Boolean,
    isFlipped270: Boolean,
    flippedX: Boolean,
    autodraw: { type: Boolean, default: true },
    hires: Boolean,
    dontUseWebgl: Boolean,
    bounds: Object,
    componentName: String,
    isSideBySide: Boolean,
    isDeepDeeAnnotation: Boolean
  },
  data() {
    return {
      usePixelCanvas: 0,
      processingCanvas: document.createElement("canvas"),
      lastDrawnImage: null,
      lastImgSize: defaultSize()
    };
  },
  computed: {
    ...mapGetters(["hasWebgl", "webglMaxTexture"]),
    exceedMaxTexture(){
      return(
        this.imgSize.h > this.webglMaxTexture || this.imgSize.w > this.webglMaxTexture
      );
    },
    processedImg() {
      return (
        (!this.useWebgl &&
          this.needsShading &&
          this.usePixelCanvas &&
          this.processingCanvas) ||
        this.img
      );
    },
    needsShading() {
      return (
        this.needsPixelShading ||
        this.needsOctShading ||
        this.needsThicknessShading
      );
    },
    needsPixelShading() {
      return (
        this.colorSettings && hasCustomPixelColorSettings(this.colorSettings)
      );
    },
    needsOctShading() {
      return (
        this.octColorSettings &&
        hasCustomOCTColorSettings(this.octColorSettings)
      );
    },
    needsThicknessShading() {
      return (
        this.thicknessMapColorSettings &&
        hasCustomThicknessColorSettings(this.thicknessMapColorSettings)
      );
    },
    pixelShaderSettings() {
      if (this.needsPixelShading)
        return { rule: applyPixelShadingRules, settings: this.colorSettings };
      if (this.needsThicknessShading)
        return {
          rule: applyOCTPixelShading,
          settings: this.thicknessMapColorSettings
        };
      if (this.needsOctShading)
        return { rule: applyOCTPixelShading, settings: this.octColorSettings };
      return {};
    },
    blankCanvas() {
      return (
        !this.canvasSize || this.canvasSize.w <= 1 || this.canvasSize.h <= 1
      );
    },
    useWebgl(){
      return this.hasWebgl && !this.dontUseWebgl && !this.exceedMaxTexture;
    }
  },
  methods: {
    processImageForDisplay(size) {
      if (this.blankCanvas) return;
      if (!size) return;
      if (!this.img) return this.stopUsingCanvas();

      if (this.useWebgl) this.resizeWebglImage(size);
      else this.updateImageShading(size);
    },
    updateImageShading(size) {
      const { rule, settings } = this.pixelShaderSettings;
      if (!rule || !settings) return this.stopUsingCanvas();
      setMinimalSize(this.processingCanvas, this.img, size);
      drawShadedImage(this.img, this.processingCanvas, rule, settings);
      this.usePixelCanvas++;
      this.$refs.canvasImage.draw();
    },
    stopUsingCanvas() {
      this.usePixelCanvas = 0;
    },
    resizeWebglImage(size) {
      // don't bother with the resize trick for small images
      if (this.getSizeDifference(size) < 1000000)
        return this.stopUsingCanvas();

      // if we aren't requesting a different image size, don't bother recalculating
      if (this.lastDrawnImage == this.img 
        && size.w === this.lastImgSize.w 
        && size.h === this.lastImgSize.h
        && !this.isDeepDeeAnnotation) 
        return;

      setMinimalSize(this.processingCanvas, this.img, size);
      let ctx = this.processingCanvas.getContext("2d");
      ctx.drawImage(this.img, 0, 0, this.processingCanvas.width, this.processingCanvas.height);
      this.lastDrawnImage = this.img;
      setSize(this.lastImgSize, size);
      this.usePixelCanvas++;
      this.$refs.canvasWebgl.draw();
    },
    getSizeDifference(size){
      return this.img.width * this.img.height - size.w * size.h;
    },
    loadFullSize: debounce(function() {
      let size = defaultSize();
      setSize(size, this.img);
      this.processImageForDisplay(size);
    }, 100)
  },
  watch: {
    img() {
      this.processImageForDisplay(this.canvasSize);
      if (this.hires) this.loadFullSize();
    },
    canvasSize: {
      handler() {
        this.processImageForDisplay(this.canvasSize);
        if (this.hires) this.loadFullSize();
      },
      deep: true
    },
    colorSettings: {
      handler() {
        this.processImageForDisplay(this.canvasSize);
        if (this.hires && !this.useWebgl) this.loadFullSize();
      },
      deep: true
    },
    octColorSettings: {
      handler() {
        this.processImageForDisplay(this.canvasSize);
        if (this.hires && !this.useWebgl) this.loadFullSize();
      },
      deep: true
    },
    thicknessMapColorSettings: {
      handler() {
        this.processImageForDisplay(this.canvasSize);
        if (this.hires && !this.useWebgl) this.loadFullSize();
      },
      deep: true
    },
    isDeepDeeAnnotation: {
      handler() {
        this.processImageForDisplay(this.canvasSize);
        if (this.hires && !this.useWebgl) this.loadFullSize();
      },
      deep: true
    }
  },
  mounted() {
    this.processImageForDisplay(this.canvasSize);
    if (this.hires) this.loadFullSize();
  }
};
</script>